/**
 * @fileoverview Change-password API call.
 * @description The one function that calls POST /identity/password/change, through the shared Axios
 * client. Requires the current password; succeeding revokes every session — this one included — so
 * the caller must sign in again (Login + MFA).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { httpClient } from '@/shared/api/http-client'

export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export interface ChangePasswordResponse {
  passwordChanged: true
  /** A fresh Login + MFA is required. */
  loginRequired: true
  /** Every session was revoked. */
  sessionsRevoked: true
}

/**
 * @description Changes the signed-in user's own password.
 * @param {ChangePasswordRequest} payload - The current password (for verification), the new one,
 * and its confirmation.
 * @returns {Promise<ChangePasswordResponse>} Confirmation that every session was revoked.
 */
export async function changePassword(
  payload: ChangePasswordRequest,
): Promise<ChangePasswordResponse> {
  const { data } = await httpClient.post<ChangePasswordResponse>(
    '/identity/password/change',
    payload,
  )
  return data
}
